import type { Lot, Section } from '../../types/inventory';

export const MIN_LOT_SIZE = 20;
export const PAD_X = 24;
export const PAD_TOP = 40;
export const PAD_BOTTOM = 16;
export interface Bounds { x: number; y: number; width: number; height: number }
export interface Viewport { x: number; y: number; scale: number }

export function lotBounds(lot: Lot, section: Section): Bounds {
  return {
    x: lot.isFreeform ? lot.x_coord : section.x + PAD_X + lot.col * (section.lotWidth + section.lotGap),
    y: lot.isFreeform ? lot.y_coord : section.y + PAD_TOP + lot.row * (section.lotHeight + section.lotGap),
    width: lot.width || section.lotWidth,
    height: lot.height || section.lotHeight,
  };
}

export function unionBounds(items: Bounds[]): Bounds {
  if (!items.length) return { x: 0, y: 0, width: 800, height: 600 };
  const x = Math.min(...items.map(item => item.x));
  const y = Math.min(...items.map(item => item.y));
  return { x, y, width: Math.max(...items.map(item => item.x + item.width)) - x,
    height: Math.max(...items.map(item => item.y + item.height)) - y };
}

export function sectionBounds(section: Section, lots: Lot[]): Bounds {
  const grid = section.layoutMode !== 'FREEFORM';
  const base = { x: section.x, y: section.y,
    width: grid ? PAD_X * 2 + section.cols * section.lotWidth + Math.max(0, section.cols - 1) * section.lotGap : section.width,
    height: grid ? PAD_TOP + PAD_BOTTOM + section.rows * section.lotHeight + Math.max(0, section.rows - 1) * section.lotGap : section.height };
  return unionBounds([base, ...lots.map(lot => {
    const bounds = lotBounds(lot, section);
    return { x: bounds.x - PAD_X, y: bounds.y - PAD_TOP, width: bounds.width + PAD_X * 2, height: bounds.height + PAD_TOP + PAD_BOTTOM };
  })]);
}

export function fitViewport(bounds: Bounds, width: number, height: number): Viewport {
  const scale = Math.max(0.01, Math.min(1.5, (width - 64) / Math.max(1, bounds.width), (height - 64) / Math.max(1, bounds.height)));
  return { scale, x: (width - bounds.width * scale) / 2 - bounds.x * scale,
    y: (height - bounds.height * scale) / 2 - bounds.y * scale };
}

export function zoomAt(view: Viewport, point: { x: number; y: number }, scale: number): Viewport {
  const next = Math.max(0.01, Math.min(4, scale));
  return { scale: next, x: point.x - (point.x - view.x) / view.scale * next,
    y: point.y - (point.y - view.y) / view.scale * next };
}

export function moveSectionLots(lots: Lot[], section: Section, x: number, y: number): Lot[] {
  return lots.map(lot => {
    if (lot.sectionId !== section.id) return lot;
    const bounds = lotBounds(lot, section);
    return { ...lot, x_coord: bounds.x + x - section.x, y_coord: bounds.y + y - section.y };
  });
}
